import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IEventHubsDAO, IHubAttendeeListItemDTO, IHubAttendeesListDTO, IHubOrganizationDTO, THubAttendeesSortKeys } from '../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../api/implementations/event-hubs.dao';
import { IHubRegistrationConfigurationDAO, IHubRegistrationField } from '../../../../api/hub-registration-configuration.dao';
import { HubRegistrationConfigurationDAO } from '../../../../api/implementations/hub-registration-configuration.dao';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { ISubWindowsService } from '../../../../infrastructure/sub-windows';
import { SortOrder } from '../../../../api/shared.dto';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { HubAttendeeForm } from '../_hub-attendee-form/hub-attendee-form';
import { HubOrganizationForm } from '../_hub-organization-form/hub-organization-form';
import { EDIT_HUB_ATTENDEE_FORM_TITLE_TEXT, EDIT_HUB_ORGANIZATION_FORM_TITLE_TEXT, HUB_ATTENDEE_FORM_TITLE_TEXT } from '../../event-hubs.text';
import {
    DEFAULT_HUB_ATTENDEE_COLUMNS,
    getDeselectedCustomFieldsStorageKey,
    HUB_ATTENDEES_COLUMNS_STORAGE_KEY,
    IHubCustomFieldColumn,
    normalizeFixedColumns,
    parseColumnIds,
    parseCustomFieldIds,
    parseDeselectedCustomFieldIds,
    THubAttendeeColumnId,
    THubAttendeeFixedColumnId,
    toCustomFieldColumnId,
} from './hub-attendee-columns';
import { errorText } from '../../../../constants/common-labels.constants';


type THubFormWireField = IHubRegistrationField & { customFieldId?: number };

export class EventHubAttendeesViewModel {
    @mobx.observable
    private attendees: IHubAttendeeListItemDTO[] = [];

    @mobx.observable
    private fetchingAttendees: boolean = false;

    @mobx.observable
    private sortOrder: SortOrder;

    @mobx.observable
    private sortField: THubAttendeesSortKeys;

    @mobx.observable
    public searchQuery: string = '';

    @mobx.observable
    private customFields: IHubCustomFieldColumn[] = [];

    @mobx.observable
    private customFieldsLoaded: boolean = false;

    @mobx.observable
    private fixedColumnIds: THubAttendeeFixedColumnId[] = DEFAULT_HUB_ATTENDEE_COLUMNS;

    @mobx.observable
    private deselectedCustomFieldIds: number[] = [];

    private paginationVM: PagingViewModel;

    constructor(
        public readonly accountId: number,
        public readonly eventHubId: number,
        @IoC.Inject private readonly eventHubsDAO: IEventHubsDAO = IoC.Container.get(EventHubsDAO),
        @IoC.Inject private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowsService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly hubRegistrationConfigurationDAO: IHubRegistrationConfigurationDAO = IoC.Container.get(HubRegistrationConfigurationDAO),
    ) {
        this.sortField = 'lastName';
        this.sortOrder = SortOrder.Asc;
        this.paginationVM = new PagingViewModel();
        this.loadStoredColumns();

        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAttendees);
    }

    public getPagingVM(): PagingViewModel {
        return this.paginationVM;
    }

    public isFetchingAttendees(): boolean {
        return this.fetchingAttendees;
    }

    public getAttendees(): IHubAttendeeListItemDTO[] {
        return this.attendees;
    }

    public getSortField(): THubAttendeesSortKeys {
        return this.sortField;
    }

    public getSortOrder(): SortOrder {
        return this.sortOrder;
    }

    public getCustomFields(): IHubCustomFieldColumn[] {
        return this.customFields;
    }

    public areCustomFieldsLoaded(): boolean {
        return this.customFieldsLoaded;
    }

    public getVisibleColumnIds(): THubAttendeeColumnId[] {
        const visibleCustomColumnIds = this.customFieldsLoaded
            ? this.customFields
                .filter(field => !this.deselectedCustomFieldIds.includes(field.customFieldId))
                .map(field => toCustomFieldColumnId(field.customFieldId))
            : [];
        return [...this.fixedColumnIds, ...visibleCustomColumnIds];
    }

    public isDefaultColumnSelection(): boolean {
        const defaultColumnIds: THubAttendeeColumnId[] = [
            ...DEFAULT_HUB_ATTENDEE_COLUMNS,
            ...this.customFields.map(field => toCustomFieldColumnId(field.customFieldId)),
        ];
        const visibleColumnIds = this.getVisibleColumnIds();
        return defaultColumnIds.length === visibleColumnIds.length && defaultColumnIds.every(id => visibleColumnIds.includes(id));
    }

    @mobx.action
    public setSortParams = (sortField: THubAttendeesSortKeys, sortOrder: SortOrder): void => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.fetchAttendees();
    };

    @mobx.action
    public fetchAttendees = async (): Promise<void> => {
        this.fetchingAttendees = true;
        try {
            const result = await this.eventHubsDAO.getHubAttendees({
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                sortField: this.sortField,
                sortOrder: this.sortOrder,
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE(),
            });
            this.onFetchSuccess(result);
        } catch (error) {
            this.onFetchFail(error);
        }
    };

    @mobx.action
    public fetchCustomFields = async (): Promise<void> => {
        try {
            const configuration = await this.hubRegistrationConfigurationDAO.getRegistrationFormConfiguration(this.accountId, this.eventHubId);
            this.onCustomFieldsFetchSuccess(configuration.fields as THubFormWireField[]);
        } catch (error) {
            this.onCustomFieldsFetchFail(error);
        }
    };

    @mobx.action
    public applyColumnSelection = (columns: string): void => {
        const columnIds = parseColumnIds(columns);
        const selectedCustomFieldIds = new Set(parseCustomFieldIds(columnIds));
        const fixedColumnIds = normalizeFixedColumns(columnIds);
        const isGarbageSelection = columnIds.length > 0 && fixedColumnIds.length === 1 && !selectedCustomFieldIds.size;
        if (isGarbageSelection) {
            this.resetColumns();
            return;
        }

        const knownCustomFieldIds = new Set(this.customFields.map(field => field.customFieldId));
        this.deselectedCustomFieldIds = [
            ...this.deselectedCustomFieldIds.filter(id => !knownCustomFieldIds.has(id)),
            ...this.customFields.map(field => field.customFieldId).filter(id => !selectedCustomFieldIds.has(id)),
        ];
        this.fixedColumnIds = fixedColumnIds;
        this.saveStoredColumns();
    };

    @mobx.action
    public resetColumns = (): void => {
        this.fixedColumnIds = DEFAULT_HUB_ATTENDEE_COLUMNS;
        this.deselectedCustomFieldIds = [];
        localStorage.setItem(HUB_ATTENDEES_COLUMNS_STORAGE_KEY, DEFAULT_HUB_ATTENDEE_COLUMNS.join());
        localStorage.removeItem(getDeselectedCustomFieldsStorageKey(this.eventHubId));
    };

    @mobx.action
    public clearAttendees = (): void => {
        this.attendees = [];
    };

    public addAttendee = (): void => {
        this.subWindowsService.showSubWindow({
            title: HUB_ATTENDEE_FORM_TITLE_TEXT.defaultMessage,
            componentToHost: HubAttendeeForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                saveCompletedCallback: () => {
                    this.fetchAttendees();
                },
            },
        });
    };

    public editAttendee = (attendee: IHubAttendeeListItemDTO): void => {
        const facade = this.subWindowsService.showSubWindow({
            title: EDIT_HUB_ATTENDEE_FORM_TITLE_TEXT.defaultMessage,
            componentToHost: HubAttendeeForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                attendeeId: attendee.attendeeId,
                initialAttendee: attendee,
                saveCompletedCallback: () => {
                    this.fetchAttendees();
                },
                deleteHubAttendeeOnList: () => {
                    facade.close();
                    this.fetchAttendees();
                },
            },
        });
    };

    public editHubOrganizationForAttendee = async (attendee: IHubAttendeeListItemDTO): Promise<void> => {
        const organizationId = attendee.organizationId;
        if (organizationId == null) {
            return;
        }
        let initialHubOrganization: IHubOrganizationDTO;
        try {
            initialHubOrganization = await this.eventHubsDAO.getHubOrganization(this.accountId, this.eventHubId, organizationId);
        } catch (error) {
            const errorMessage = this.errorParsing.extractMessage(error);
            alertServerError('API Error fetching hub organization details', errorMessage);
            return;
        }
        const facade = this.subWindowsService.showSubWindow({
            title: EDIT_HUB_ORGANIZATION_FORM_TITLE_TEXT.defaultMessage,
            componentToHost: HubOrganizationForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                closeHubOrganizationPopup: () => {
                    facade.close();
                },
                initialHubOrganization: initialHubOrganization,
                updateOrganizationOnList: () => {
                    this.fetchAttendees();
                },
            },
        });
    };

    @mobx.action
    private loadStoredColumns(): void {
        const storedColumns = localStorage.getItem(HUB_ATTENDEES_COLUMNS_STORAGE_KEY);
        if (storedColumns != null) {
            const storedColumnIds = parseColumnIds(storedColumns);
            const fixedColumnIds = normalizeFixedColumns(storedColumnIds);
            // An empty stored value means "mandatory column only", a value without any valid column falls back to the defaults.
            const hasNoValidColumn = storedColumnIds.length > 0 && fixedColumnIds.length === 1;
            this.fixedColumnIds = hasNoValidColumn ? DEFAULT_HUB_ATTENDEE_COLUMNS : fixedColumnIds;
        }
        this.deselectedCustomFieldIds = parseDeselectedCustomFieldIds(localStorage.getItem(getDeselectedCustomFieldsStorageKey(this.eventHubId)));
    }

    private saveStoredColumns(): void {
        localStorage.setItem(HUB_ATTENDEES_COLUMNS_STORAGE_KEY, this.fixedColumnIds.join());
        if (this.deselectedCustomFieldIds.length) {
            localStorage.setItem(getDeselectedCustomFieldsStorageKey(this.eventHubId), this.deselectedCustomFieldIds.join());
        } else {
            localStorage.removeItem(getDeselectedCustomFieldsStorageKey(this.eventHubId));
        }
    }

    @mobx.action
    private onCustomFieldsFetchSuccess(wireFields: THubFormWireField[]): void {
        // The form configuration mixes default and custom fields, only custom ones have `default: false`.
        this.customFields = (wireFields || [])
            .filter(field => !field.default)
            .map(field => ({ customFieldId: field.customFieldId, label: field.label ?? '' }))
            .sort((a, b) => a.customFieldId - b.customFieldId);

        this.customFieldsLoaded = true;
    }

    @mobx.action
    private onCustomFieldsFetchFail(error: unknown): void {
        this.customFieldsLoaded = true;
        alertServerError(errorText(), this.errorParsing.extractMessage(error));
    }

    @mobx.action
    private onFetchSuccess(data: IHubAttendeesListDTO): void {
        this.attendees = data.content;
        this.fetchingAttendees = false;
        this.paginationVM.setTotalRecords(data.totalElements);
    }

    @mobx.action
    private onFetchFail(error: unknown): void {
        this.fetchingAttendees = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error fetching hub attendee records', errorMessage);
    }
}
